import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  fetchAIRecommendations,
  fetchAIConfig,
  fetchAIConnectionStatus,
  fetchAITradeStatus,
  fetchScheduledOrders,
  fetchPurchaseAnalyses,
  fetchTradingSettings,
  cancelScheduledOrder,
  holdAnalysisSchedule,
  setSymbolTradeBan,
} from '../../../services/stockai/api/client';
import {
  formatKrw,
  getNaverFinanceUrl,
  goToTrade,
  normalizeSelectedStock,
  STOCKAI_PREFIX,
} from '../../../services/stockai/stockUtils';

function formatWhen(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ko-KR');
  } catch {
    return String(value);
  }
}

export default function AI() {
  const navigate = useNavigate();
  const { loggedIn, mock, openLogin, authTick } = useOutletContext() || {};
  const [recs, setRecs] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [connection, setConnection] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [trading, setTrading] = useState({
    blocked_symbols: [],
    blocked_buy_symbols: [],
    blocked_sell_symbols: [],
  });
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState('all');
  const [analysisTarget, setAnalysisTarget] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const reloadOps = useCallback(async () => {
    const [conn, status, sched, purchase, settings] = await Promise.all([
      fetchAIConnectionStatus(),
      fetchAITradeStatus(),
      fetchScheduledOrders(),
      fetchPurchaseAnalyses(),
      fetchTradingSettings(),
    ]);
    setConnection(conn);
    setAiStatus(status);
    setSchedules((sched.orders || []).filter((o) => o.status === 'pending' || o.status === '대기'));
    setPurchases(purchase.analyses || []);
    setTrading(settings.trading || {
      blocked_symbols: [],
      blocked_buy_symbols: [],
      blocked_sell_symbols: [],
    });
  }, []);

  useEffect(() => {
    if (!mock && !loggedIn) {
      setRecs([]);
      setCfg(null);
      setConnection(null);
      setAiStatus(null);
      setSchedules([]);
      setPurchases([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all([fetchAIRecommendations(), fetchAIConfig(), reloadOps()])
      .then(([r, c]) => {
        if (cancelled) return;
        setRecs(r.recommendations || []);
        setCfg(c.config || c);
      })
      .catch((err) => {
        if (!cancelled) console.warn('[StockAI] AI 페이지 로드 실패:', err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [loggedIn, mock, authTick, reloadOps]);

  const filteredRecs = useMemo(() => {
    if (selectedAction === 'all') return recs;
    return recs.filter((item) => item.action === selectedAction);
  }, [recs, selectedAction]);

  const topRec = filteredRecs[0] || recs[0];
  const schedule = cfg?.execution_schedule || {};
  const analysisCfg = cfg?.analysis_config || {};

  const runAction = async (key, fn) => {
    setBusyKey(key);
    setActionMsg('');
    setActionErr('');
    try {
      const result = await fn();
      setActionMsg(result?.message || '처리되었습니다.');
      await reloadOps();
    } catch (err) {
      setActionErr(err.message || '처리 실패');
    } finally {
      setBusyKey('');
    }
  };

  const handleCancelSchedule = (order) => {
    if (!window.confirm(`${order.stock_name || order.symbol} 예약 주문을 취소할까요?`)) return;
    runAction(`cancel-${order.id}`, () => cancelScheduledOrder(order.id));
  };

  const handleWaitSchedule = (order) => {
    const analysisId = order.analysis_id
      || purchases.find((p) => String(p.scheduled_order_id) === String(order.id))?.id;
    if (!analysisId) {
      setActionErr('연결된 분석이 없어 대기로 전환할 수 없습니다. 취소를 사용하세요.');
      return;
    }
    runAction(`wait-${order.id}`, () =>
      holdAnalysisSchedule(analysisId).then(async (r) => {
        await cancelScheduledOrder(order.id).catch(() => null);
        return r;
      })
    );
  };

  const handleBanSchedule = (order) => {
    const mode = order.order_type === 'sell' ? 'sell' : 'both';
    const label = mode === 'sell' ? '매도 금지' : '거래 금지';
    if (!window.confirm(`${order.stock_name || order.symbol}을(를) ${label} 목록에 추가할까요?`)) return;
    runAction(`ban-${order.id}`, async () => {
      await setSymbolTradeBan(order.symbol, mode, true);
      if (order.id) await cancelScheduledOrder(order.id).catch(() => null);
      return { message: `${label} 적용 및 예약 취소` };
    });
  };

  const renderRecActions = (rec) => {
    const stock = normalizeSelectedStock(rec, 'recommendation');
    const orderType = rec.action === 'sell' ? 'sell' : 'buy';
    return (
      <div className="stockai-action-row">
        <button
          type="button"
          className="stockai-btn stockai-btn--sm"
          onClick={() => goToTrade(navigate, stock, orderType)}
        >
          주문으로 보내기
        </button>
        <button
          type="button"
          className="stockai-btn stockai-btn--sm stockai-btn--ghost"
          onClick={() => setAnalysisTarget(stock)}
        >
          추가 분석
        </button>
        <a
          className="stockai-btn stockai-btn--sm stockai-btn--ghost stockai-external-link"
          href={getNaverFinanceUrl(rec.symbol)}
          target="_blank"
          rel="noopener noreferrer"
        >
          네이버 증권
        </a>
      </div>
    );
  };

  if (loading) return <p className="stockai-card">로딩 중...</p>;

  return (
    <div>
      {!mock && !loggedIn && (
        <div className="stockai-alert">
          AI 분석은 로그인 후 이용할 수 있습니다.{' '}
          <button type="button" className="stockai-btn stockai-btn--sm" onClick={openLogin}>로그인</button>
        </div>
      )}
      <div className="stockai-kicker">Recommendation Station</div>
      <section className="stockai-hero">
        <div className="stockai-hero__panel">
          <h1 className="stockai-hero__headline">추천 신호와 설정 상태를 한 번에 읽는 AI 스테이션</h1>
          <p className="stockai-hero__lead">
            분석 스케줄·AI 연결·구매/거래 스케줄과 금지 설정을 한 화면에서 운영합니다.
          </p>
          <div className="stockai-chip-list">
            <button type="button" className={`stockai-chip stockai-chip--button ${selectedAction === 'all' ? 'stockai-chip--active' : ''}`} onClick={() => setSelectedAction('all')}>전체</button>
            <button type="button" className={`stockai-chip stockai-chip--button ${selectedAction === 'buy' ? 'stockai-chip--active' : ''}`} onClick={() => setSelectedAction('buy')}>매수</button>
            <button type="button" className={`stockai-chip stockai-chip--button ${selectedAction === 'sell' ? 'stockai-chip--active' : ''}`} onClick={() => setSelectedAction('sell')}>매도</button>
          </div>
        </div>

        <div className="stockai-hero__aside">
          <div className="stockai-card stockai-stat-card">
            <div className="stockai-metric">
              <span className="stockai-metric__eyebrow">추천 건수</span>
              <strong className="stockai-metric__value">{filteredRecs.length}</strong>
              <span className="stockai-metric__meta">현재 필터 기준 활성 추천</span>
            </div>
          </div>
          <div className="stockai-card stockai-stat-card">
            <div className="stockai-metric">
              <span className="stockai-metric__eyebrow">매수 기준</span>
              <strong className="stockai-metric__value">{cfg?.auto_buy?.confidence_threshold ?? 70}%</strong>
              <span className="stockai-metric__meta">자동매수 {cfg?.auto_buy?.enabled ? 'ON' : 'OFF'}</span>
            </div>
          </div>
        </div>
      </section>

      {(actionMsg || actionErr) && (
        <div className={`stockai-alert ${actionMsg ? 'stockai-alert--info' : ''}`}>
          {actionMsg || actionErr}
        </div>
      )}

      <section className="stockai-section">
        <div className="stockai-panel-grid">
          <div className="stockai-card">
            <div className="stockai-section__header">
              <div>
                <h2 className="stockai-section__title">분석 스케줄</h2>
                <div className="stockai-section__subtitle">AI 분석·실행 윈도우와 최근 분석 시각</div>
              </div>
            </div>
            <ul className="stockai-mini-list">
              <li>
                <strong>실행 모드</strong>
                <div className="stockai-stat-label">{schedule.mode || 'immediate'}</div>
              </li>
              <li>
                <strong>실행 창</strong>
                <div className="stockai-stat-label">
                  {schedule.window_start || '09:10'} – {schedule.window_end || '14:50'}
                  {schedule.enforce_market_hours !== false ? ' · 정규장만' : ''}
                </div>
              </li>
              <li>
                <strong>분석 주기</strong>
                <div className="stockai-stat-label">{analysisCfg.update_frequency_weeks ?? 1}주</div>
              </li>
              <li>
                <strong>최근 분석</strong>
                <div className="stockai-stat-label">{formatWhen(aiStatus?.last_analysis_time)}</div>
              </li>
            </ul>
          </div>

          <div className="stockai-card">
            <div className="stockai-section__header">
              <div>
                <h2 className="stockai-section__title">AI 연결 상태</h2>
                <div className="stockai-section__subtitle">Google / Local AI 연결</div>
              </div>
              <span className={`stockai-badge ${aiStatus?.active ? '' : 'stockai-badge--muted'}`}>
                {aiStatus?.active ? 'Active' : 'Idle'}
              </span>
            </div>
            <div className="stockai-conn-grid">
              <div className="stockai-conn-card">
                <span className={`stockai-conn-dot ${connection?.google_ai?.connected ? 'is-on' : ''}`} />
                <div>
                  <strong>Google AI</strong>
                  <div className="stockai-stat-label">
                    {connection?.google_ai?.connected ? '연결됨' : '미연결'}
                    {connection?.google_ai?.model ? ` · ${connection.google_ai.model}` : ''}
                  </div>
                </div>
              </div>
              <div className="stockai-conn-card">
                <span className={`stockai-conn-dot ${connection?.local_ai?.connected ? 'is-on' : ''}`} />
                <div>
                  <strong>Local AI</strong>
                  <div className="stockai-stat-label">
                    {connection?.local_ai?.connected ? '연결됨' : '미연결'}
                    {connection?.local_ai?.message ? ` · ${connection.local_ai.message}` : ''}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-panel-grid">
          <div className="stockai-card">
            <div className="stockai-section__header">
              <div>
                <h2 className="stockai-section__title">구매 스케줄</h2>
                <div className="stockai-section__subtitle">구매예정 분석 {purchases.length}건</div>
              </div>
            </div>
            {purchases.length === 0 ? (
              <p className="stockai-stat-label">구매예정 항목이 없습니다.</p>
            ) : (
              <ul className="stockai-order-list">
                {purchases.map((item) => (
                  <li key={item.id} className="stockai-order-item">
                    <div className="stockai-order-item__main">
                      <strong>{item.stock_name || item.symbol}</strong>
                      <span className="stockai-stat-label">
                        {item.trade_config?.quantity || 0}주
                        {item.trade_config?.price ? ` · ${formatKrw(item.trade_config.price)}` : ''}
                      </span>
                      <span className="stockai-stat-label">
                        예약 {formatWhen(item.trade_config?.scheduled_time)}
                      </span>
                    </div>
                    <span className="stockai-badge">{item.current_status || '구매예정'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="stockai-card">
            <div className="stockai-section__header">
              <div>
                <h2 className="stockai-section__title">거래 금지 설정</h2>
                <div className="stockai-section__subtitle">매수·매도·전체 금지 종목</div>
              </div>
            </div>
            <ul className="stockai-mini-list">
              <li>
                <strong>거래 금지 (매수·매도)</strong>
                <div className="stockai-stat-label">
                  {(trading.blocked_symbols || []).length
                    ? trading.blocked_symbols.join(', ')
                    : '없음'}
                </div>
              </li>
              <li>
                <strong>매수 금지</strong>
                <div className="stockai-stat-label">
                  {(trading.blocked_buy_symbols || []).length
                    ? trading.blocked_buy_symbols.join(', ')
                    : '없음'}
                </div>
              </li>
              <li>
                <strong>매도 금지</strong>
                <div className="stockai-stat-label">
                  {(trading.blocked_sell_symbols || []).length
                    ? trading.blocked_sell_symbols.join(', ')
                    : '없음'}
                </div>
              </li>
            </ul>
            <p className="stockai-stat-label" style={{ marginTop: '0.75rem' }}>
              스케줄 행의 [금지] 또는 포트폴리오의 매도금지로 추가할 수 있습니다.
            </p>
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-card">
          <div className="stockai-section__header">
            <div>
              <h2 className="stockai-section__title">거래 스케줄</h2>
              <div className="stockai-section__subtitle">대기 중 예약 주문 — 취소 / 대기 / 금지</div>
            </div>
            <span className="stockai-badge">{schedules.length}건</span>
          </div>
          {schedules.length === 0 ? (
            <p className="stockai-stat-label">대기 중인 거래 스케줄이 없습니다.</p>
          ) : (
            <ul className="stockai-order-list">
              {schedules.map((order) => (
                <li key={order.id} className="stockai-order-item stockai-order-item--schedule">
                  <div className="stockai-order-item__main">
                    <strong>
                      {order.order_type === 'sell' ? '매도' : '매수'} {order.stock_name || order.symbol}
                    </strong>
                    <span className="stockai-stat-label">
                      {order.quantity}주 · {formatKrw(order.price)} · {formatWhen(order.scheduled_time)}
                    </span>
                    <span className="stockai-order-status stockai-order-status--pending">대기</span>
                  </div>
                  <div className="stockai-action-row">
                    <button
                      type="button"
                      className="stockai-btn stockai-btn--sm stockai-btn--ghost stockai-btn--danger"
                      disabled={busyKey === `cancel-${order.id}`}
                      onClick={() => handleCancelSchedule(order)}
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      className="stockai-btn stockai-btn--sm stockai-btn--ghost"
                      disabled={busyKey === `wait-${order.id}`}
                      onClick={() => handleWaitSchedule(order)}
                    >
                      대기
                    </button>
                    <button
                      type="button"
                      className="stockai-btn stockai-btn--sm"
                      disabled={busyKey === `ban-${order.id}`}
                      onClick={() => handleBanSchedule(order)}
                    >
                      금지
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-panel-grid">
          <div className="stockai-card">
            <div className="stockai-section__header">
              <div>
                <h2 className="stockai-section__title">대표 추천</h2>
                <div className="stockai-section__subtitle">현재 필터에서 가장 우선 순위가 높은 추천입니다.</div>
              </div>
              {topRec && <span className="stockai-badge">{topRec.confidence}% confidence</span>}
            </div>
            {topRec ? (
              <>
                <h3 className="stockai-page-title" style={{ marginBottom: '0.5rem' }}>{topRec.name}</h3>
                <div className="stockai-chip-list" style={{ marginBottom: '1rem' }}>
                  <span className="stockai-chip">{topRec.symbol}</span>
                  <span className="stockai-chip">{topRec.action === 'buy' ? '매수 제안' : '매도 제안'}</span>
                </div>
                <p className="stockai-stat-label">{topRec.reason || '추천 사유가 준비되지 않았습니다.'}</p>
                <div className="stockai-section" style={{ marginTop: '1rem' }}>
                  {renderRecActions(topRec)}
                </div>
              </>
            ) : (
              <p className="stockai-stat-label">추천 종목이 없습니다.</p>
            )}
          </div>

          <div className="stockai-card">
            <div className="stockai-section__header">
              <div>
                <h2 className="stockai-section__title">설정 연동</h2>
                <div className="stockai-section__subtitle">추천 품질을 조정하는 현재 설정 상태입니다.</div>
              </div>
            </div>
            {cfg ? (
              <ul className="stockai-mini-list">
                <li>
                  <strong>자동매수</strong>
                  <div className="stockai-stat-label">{cfg.auto_buy?.enabled ? '활성' : '비활성'} / 기준 {cfg.auto_buy?.confidence_threshold ?? 70}%</div>
                </li>
                <li>
                  <strong>자동매도</strong>
                  <div className="stockai-stat-label">{cfg.auto_sell?.enabled ? '활성' : '비활성'}</div>
                </li>
              </ul>
            ) : (
              <p className="stockai-stat-label">설정을 불러오지 못했습니다.</p>
            )}
            <div className="stockai-cta-stack stockai-section">
              <Link className="stockai-btn" to={`${STOCKAI_PREFIX}/settings`}>상세 설정 열기</Link>
              <Link className="stockai-btn stockai-btn--ghost" to={`${STOCKAI_PREFIX}/trade`}>주문 콘솔 이동</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="stockai-section">
        <div className="stockai-section__header">
          <div>
            <h2 className="stockai-section__title">추천 리스트</h2>
            <div className="stockai-section__subtitle">추천 이유와 추천 강도를 읽기 쉬운 카드형 목록으로 구성했습니다.</div>
          </div>
        </div>
        <div className="stockai-grid stockai-grid--2">
          {filteredRecs.length === 0 ? (
            <div className="stockai-card">
              <p className="stockai-stat-label">현재 조건에 맞는 추천이 없습니다.</p>
            </div>
          ) : (
            filteredRecs.map((r) => (
              <div key={r.symbol} className="stockai-card stockai-feature-card">
                <div className="stockai-setting-row">
                  <strong>{r.name}</strong>
                  <span className="stockai-badge">{r.confidence ?? 0}%</span>
                </div>
                <div className="stockai-chip-list" style={{ marginBottom: '0.85rem' }}>
                  <span className="stockai-chip">{r.symbol}</span>
                  <span className="stockai-chip">{r.action === 'buy' ? '매수' : r.action}</span>
                </div>
                <p className="stockai-stat-label">{r.reason || '추천 근거가 없습니다.'}</p>
                <div className="stockai-section" style={{ marginTop: '0.85rem' }}>
                  {renderRecActions(r)}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {analysisTarget && (
        <section className="stockai-section">
          <div className="stockai-card">
            <div className="stockai-section__header">
              <div>
                <h2 className="stockai-section__title">추가 분석 — {analysisTarget.name}</h2>
                <div className="stockai-section__subtitle">
                  AI 추천 근거를 바탕으로 한 내부 요약입니다. 상세 차트·뉴스는 네이버 증권에서 확인하세요.
                </div>
              </div>
              <button
                type="button"
                className="stockai-btn stockai-btn--sm stockai-btn--ghost"
                onClick={() => setAnalysisTarget(null)}
              >
                닫기
              </button>
            </div>
            <div className="stockai-chip-list" style={{ marginBottom: '0.75rem' }}>
              <span className="stockai-chip">{analysisTarget.symbol}</span>
              {analysisTarget.confidence != null && (
                <span className="stockai-badge">신뢰도 {analysisTarget.confidence}%</span>
              )}
            </div>
            <p className="stockai-stat-label">
              {analysisTarget.reason || '상세 분석 데이터가 아직 준비되지 않았습니다.'}
            </p>
            <div className="stockai-action-row stockai-section">
              <button
                type="button"
                className="stockai-btn stockai-btn--sm"
                onClick={() => goToTrade(navigate, analysisTarget, analysisTarget.orderType)}
              >
                이 종목 주문하기
              </button>
              <a
                className="stockai-btn stockai-btn--sm stockai-btn--ghost stockai-external-link"
                href={getNaverFinanceUrl(analysisTarget.symbol)}
                target="_blank"
                rel="noopener noreferrer"
              >
                네이버 증권에서 상세 보기
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
